import { Adapter } from "@core/adapter";
import { MediaAdapter } from '@core/adapters/media.adapter';
import { Place, PlaceAdapter } from '../place.model';
import { Teacher } from '../teacher.model';
import { MediaFile } from "../media-file.model";
import { Injectable } from "@angular/core";
import { AddressModel } from "../address.model";
import { formatDate } from "@angular/common";
import { TranslateService } from "@ngx-translate/core";

export class Session{
  constructor(
    public id:string,
    public name:string = "",
    public date: Date | null,
    public startTime: string,
    public endTime: string,
    public teachers: Teacher[] = [],
    public files: MediaFile[] = [],
    public location: Place,
    public videoUrl: string|null = null,
    public onDemand:boolean,
    public isDigital:boolean = false,
    public content: string|null = null,
    public description: string = "",
    public availableUntil: Date| null,
    public availableUntilCalculated: Date| null,
    public amountOfDaysAvailable:string,
    public position: number,
    public presentationFile:string = '',
    public wasPresent:boolean|null = null,
    public presenceId:string|null = null,
    public evaluationFormId:string|null = null,
    public evaluationFormSubmitted:boolean = false,
    public embeddable:boolean = false,
    public streamType: string|null = null
  ){}

  public isVideoStream():boolean{
    return this.streamType === 'videostream';
  }

  /**
   * Combines the session date and startTime ("HH:mm[:ss]") into a single Date.
   * Returns null for sessions without a date (e.g. on-demand).
   */
  public getStartDateTime():Date|null{
    if(!this.date){
      return null;
    }
    const start = new Date(this.date);
    if(this.startTime){
      const [h, m, s] = this.startTime.split(':').map(p => parseInt(p, 10) || 0);
      start.setHours(h, m, s ?? 0, 0);
    }
    return start;
  }

  public getFiles(){
    if(this.onDemand && this.inThePast()){
      return [];
    }
    return this.files.filter(x => x.type =="file");
  }
  public getAttest(){
    return this.files.filter(x => x.type =="attest");
  }

  public getTotalFileCount(){
    return this.getFiles().length + this.getAttest().length
  }

  public inThePast(owned:boolean = true):boolean{
    let currentDate = new Date();
    currentDate.setHours(0,0,0,0);
    const currentTime = currentDate.getTime();

    if(this.onDemand){
      if(owned){
        if(this.availableUntilCalculated){
          return new Date(this.availableUntilCalculated).getTime() < currentTime;
        }else if(this.availableUntil){
            return new Date(this.availableUntil).getTime() < currentTime;
        }else{
          return false;
        }
      }else{

        if(this.availableUntil){
         return new Date(this.availableUntil).getTime() < currentTime;
        }else{
          return false;
        }
      }
    }else{
      if(this.date){
        return new Date(this.date).getTime() < currentTime;
      }else{
        return true;
      }
    }
  }
}


@Injectable({
  providedIn: 'root'
})

export class SessionAdapter implements Adapter<Session> {
  constructor(private placeAdapter: PlaceAdapter, private mediaAdapter: MediaAdapter|null, private translateService?: TranslateService) {
  }

  adapt(item: any): Session {
    let sessionName = item.name;
    if(!sessionName){
      if(item.onDemand){
        sessionName = 'On demand';
      }else if(this.translateService){
        if(item.date){
          sessionName = formatDate(item.date,'EEEE', this.translateService.getCurrentLang() ?? 'nl');
        }
        sessionName = sessionName.charAt(0).toUpperCase() + sessionName.substr(1);
      }
    }

    const presentationFile = item.presentationFile ? (item.presentationFile?.fileObject?.fileUrl) : ''
    let session = new Session(
      item.id,
      sessionName,
      item.date,
      item.startTime,
      item.endTime,
      (item.teachers??[]).map( (teacher:any) => {return {id:teacher.id, salutation:teacher.salutation, firstName:teacher.firstName, lastName:teacher.lastName, email:teacher.email??'', description:teacher.description??'', image: teacher.image ? teacher.image.fileObject.fileUrl+'?filter=thumb&width=60&height=60&mode=outbound':''}}),
      (item.mediaFiles ?? []).map((m: any) => {
        return this.mediaAdapter ? this.mediaAdapter.adapt(m) : null
      }).filter((v: any) => !!v),
      (typeof item.location === 'undefined') ? new Place('','','', new AddressModel()) :this.placeAdapter.adapt(item.location),
      item.videoUrl || null,
      item.onDemand,
      item.onDemand || !(item.location && item.location.name),
      item.content || null,
      item.description ?? '',
      item.availableUntil || null,
      item.availableUntilCalculated || null,
      this.calcDays(item.amountOfDaysAvailable ?? 0),
      item.position ?? 0,
      presentationFile,
      null,
      null,
      item.evaluationFormId || null,
      item.evaluationFormSubmitted || false,
      item.embeddable??false,
      item.streamType ?? null
    );


    let wasPresent:boolean|null  = null;
    if(item.presences && item.presences.length){
      if(item.onDemand){
        wasPresent = item.presences[0].present ? true : null;
      }else{
        if(session.inThePast()){
          wasPresent = item.presences[0].present;
        }
      }
      session.presenceId = item.presences[0].id;
    }
    session.wasPresent = wasPresent;

    return session;
  }

  calcDays(days:number){
    if(days){
      if(days > 364){
        const years = Math.floor(days / 365);
        const yearstr = years > 1 ? " jaren" : " jaar";
        const restdays = days  - (365 * years);
        const daystr = restdays > 0 ? " en " + restdays + (restdays > 1 ? " dagen" : " dag"):'';
        return years + yearstr + (restdays?daystr:"");
      }
      return days + (days > 1 ? " dagen" : " dag");

    }
    return "";
  }
}
